import { useLazyQuery, useMutation } from "@apollo/client";
import { QuickWinStrategy } from "@towersystems/roam-common/lib/generated-types";
import React from "react";
import {
  MUTATION_UPDATE_QUICK_WIN_STRATEGY,
  QUERY_QUICK_WIN_STRATEGY_BY_CODE,
} from "../../common";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { ManageLlmStrategiesParamList } from "../../../../types";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import { FlatListSearchableSkeleton } from "../../../../components/FlatListSearchableSkeleton";
import {
  Card,
  FlatActionList,
  Page,
  Stack,
  Switch,
  TextField,
  useFlashMessages,
  Button,
} from "@towersystems/roam-common-native";
import { useCurrentVendorProvider } from "../../../../utilities/vendor";
import {
  UpdateQuickWinForm,
  UpdateQuickWinFormValues,
} from "../../components/UpdateQuickWinForm";
import { Controller, useFormContext } from "react-hook-form";
import { Colors, View } from "react-native-ui-lib";
import { useLlmQuickWinsProvider } from "../../../../utilities/llm-quick-wins";

export type ManageQuickWinHandlerProps = NativeStackScreenProps<
  ManageLlmStrategiesParamList,
  "ManageQuickWinHandlerScreen"
>;

const Content = ({
  navigation,
  quickWinStrategy,
}: ManageQuickWinHandlerProps & {
  quickWinStrategy: QuickWinStrategy;
}) => {
  const flashMessagesContext = useFlashMessages();
  const { currentVendor } = useCurrentVendorProvider();
  const { refreshStrategies } = useLlmQuickWinsProvider();

  const { control, reset, setValue, formState, handleSubmit, watch } =
    useFormContext<UpdateQuickWinFormValues>();

  const {
    isDirty,
    isLoading,
    isSubmitting,
    isValid,
    isSubmitSuccessful,
    errors,
  } = formState;

  const [m] = useMutation<{
    updateQuickWinStrategy: QuickWinStrategy;
  }>(MUTATION_UPDATE_QUICK_WIN_STRATEGY);

  React.useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <Button
          label="Restore Defaults"
          isDestructive
          onPress={() => {
            setValue(
              "instructions",
              quickWinStrategy.handler?.args.find(
                (arg) => arg.name === "instructions"
              )?.defaultValue as string,
              { shouldDirty: true }
            );
            setValue("enabled", true, {
              shouldDirty: true,
            });
          }}
          size="compact"
        />
      ),
    });
  }, [navigation, setValue, quickWinStrategy]);

  const handleOnSubmit = async (values: UpdateQuickWinFormValues) => {
    return m({
      variables: {
        input: {
          code: quickWinStrategy.code,
          enabled: values.enabled,
          config: {
            code: quickWinStrategy.config?.code!,
            arguments: [{ name: "instructions", value: values.instructions }],
          },
        },
      },
    })
      .then(async () => {
        flashMessagesContext.showMessage({
          message: "Changes saved successfully.",
          severity: "success",
        });
        console.log("Refreshing strategies...");
        await refreshStrategies();
        navigation.goBack();
      })
      .catch((error) => {
        flashMessagesContext.showMessage({
          message: "Failed to save changes. Please try again.",
          severity: "error",
        });
      });
  };

  const disabled =
    !isDirty || isLoading || isSubmitting || !isValid || isSubmitSuccessful;

  const footerMarkup = (
    <Button
      disabled={disabled}
      label={"Save"}
      backgroundColor={Colors.action}
      color="white"
      onPress={handleSubmit(handleOnSubmit)}
    ></Button>
  );

  return (
    <Page
      title={quickWinStrategy?.handler?.name}
      secondaryTitle={quickWinStrategy?.handler?.description}
      footer={footerMarkup}
    >
      <Stack vertical>
        <Card title="General">
          <FlatActionList
            items={[
              {
                key: "location_enabled",
                title: "Enabled",
                subTitle: "Toggle to enable or disable this AI feature.",
                onPress: () => {},
                componentRight: () => (
                  <Controller
                    name="enabled"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <View>
                        <Switch
                          value={value}
                          onValueChange={() => {
                            onChange(!value);
                          }}
                        />
                      </View>
                    )}
                  />
                ),
              },
            ]}
          />
        </Card>
        <Card title="Instructions">
          <Stack vertical>
            <View>
              <Controller
                control={control}
                name="instructions"
                render={({ field: { onChange, value } }) => (
                  <TextField
                    value={value}
                    onChangeText={onChange}
                    multiline
                    style={{ minHeight: 300 }}
                    textarea
                  />
                )}
              />
            </View>
          </Stack>
        </Card>
      </Stack>
    </Page>
  );
};

export const ManageQuickWinHandlerScreen = ({
  navigation,
  route,
}: ManageQuickWinHandlerProps) => {
  const [quickWin, setQuickWin] = React.useState<QuickWinStrategy | undefined>(
    undefined
  );

  const transitionEnded = useHasTransitionEnded(navigation);

  const [queryLlmStrategyById] = useLazyQuery<{
    quickWinStrategyByCode: QuickWinStrategy;
  }>(QUERY_QUICK_WIN_STRATEGY_BY_CODE);

  React.useEffect(() => {
    (async () => {
      try {
        const llmStrategyResult = await queryLlmStrategyById({
          variables: { code: route.params.quickWinHandlerCode },
        });
        console.log("llmStrategyResult", llmStrategyResult);
        setQuickWin(llmStrategyResult.data?.quickWinStrategyByCode);
      } catch (error) {
        console.error("Error fetching LLM strategy:", error);
      }
    })();
  }, [navigation, queryLlmStrategyById]);

  if (quickWin === undefined || quickWin === null || !transitionEnded) {
    return <FlatListSearchableSkeleton searchbar={false} />;
  }

  return (
    <UpdateQuickWinForm
      defaultFormValues={{
        code: quickWin.code,
        instructions:
          (quickWin.config?.args.find((arg) => arg.name === "instructions")
            ?.value as string) || "",
        enabled: quickWin.enabled,
      }}
    >
      <Content
        navigation={navigation}
        route={route}
        quickWinStrategy={quickWin}
      />
    </UpdateQuickWinForm>
  );
};
